import { ArrowLeft, SkipForward } from "lucide-react";
import DrawingBoard from "./DrawingBoard";
import { useProfileContext } from "../../../context/ProfileContext";
import WordImage from "./WordImage";
import { useNavigate } from "react-router-dom";

type Props = {
  drawingRef: React.RefObject<any>;
  onBack: () => void;
  onSubmit: () => void;
  onSkip: () => void;
  isUploading: boolean;
  uploadStatus: string;
};

const DrawingPage = ({
  drawingRef,
  onBack,
  onSubmit,
  onSkip,
  isUploading,
  uploadStatus,
}: Props) => {
  const { currentWord } = useProfileContext();
  const router = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-300 to-yellow-200 p-4 flex items-center justify-center">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-2xl w-full transform hover:scale-105 transition-all duration-300">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-800 mb-4 animate-bounce">
            🎨 Draw this word! 🎨
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            Step 2 of 2: Draw what you learned
          </p>

          {/* Clock Icon */}
          <WordImage />
        </div>

        {/* Drawing Canvas */}
        <div className="mb-6">
          <DrawingBoard ref={drawingRef} key={currentWord + "drawing"} />
        </div>

        {/* Navigation Buttons */}
        <div className="space-y-3 mb-4">
          {/* First row - Back button (full width on mobile, can be separate) */}
          <div className="flex gap-3">
            <button
              onClick={onBack}
              className="flex-1 bg-gradient-to-r from-blue-400 to-cyan-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-6 h-6" />
              <span className="hidden sm:inline">Back to Speech Practice</span>
              <span className="sm:hidden">Speech</span>
            </button>

            <button
              onClick={() => {
                router("/profile");
              }}
              className="flex-1 bg-gradient-to-r from-blue-400 to-cyan-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-6 h-6" />
              <span className="hidden sm:inline">Back to Home Page</span>
              <span className="sm:hidden">Home</span>
            </button>
          </div>

          {/* Second row - Skip and Submit buttons */}
          <div className="flex gap-3">
            <button
              onClick={onSkip}
              className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <SkipForward className="w-6 h-6" />
              <span className="hidden sm:inline">Skip Drawing</span>
              <span className="sm:hidden">Skip</span>
            </button>

            <button
              onClick={onSubmit}
              disabled={isUploading}
              className={`flex-1 bg-gradient-to-r from-emerald-400 to-teal-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 ${
                isUploading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  <span className="hidden sm:inline">Submitting...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <span className="text-2xl">📤</span>
                  <span className="hidden sm:inline">Submit !</span>
                  <span className="sm:hidden">Done!</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Upload Status */}
        {uploadStatus && (
          <div
            className={`mt-3 p-3 rounded-xl font-semibold text-center ${
              uploadStatus.includes("✅")
                ? "bg-green-100 text-green-700 border-2 border-green-300"
                : uploadStatus.includes("❌")
                ? "bg-red-100 text-red-700 border-2 border-red-300"
                : "bg-blue-100 text-blue-700 border-2 border-blue-300"
            }`}
          >
            {uploadStatus}
          </div>
        )}

        {/* Decorative Elements */}
        <div className="absolute top-4 left-4 text-4xl animate-spin">🌟</div>
        <div className="absolute top-4 right-4 text-4xl animate-bounce">🎈</div>
        <div className="absolute bottom-4 left-4 text-4xl animate-pulse">
          🌈
        </div>
        <div className="absolute bottom-4 right-4 text-4xl animate-bounce">
          🎯
        </div>
      </div>
    </div>
  );
};

export default DrawingPage;
